import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Alert, Button, Stack, Text, Title } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { wizardRatesPanelMessages } from './wizard-rates-panel-messages';
import { WizardCostBreakdown } from './WizardCostBreakdown';
import { WizardInsuranceFooter } from './WizardInsuranceFooter';
import styles from './WizardRatesPanel.module.css';
import { FREIGHT_QUOTE_ERROR_CODE } from '../../../../ltl/constants';
import { FreightNoCarriersMessage } from '../../../freight-no-carriers-message/freight-no-carriers-message';
import { FreightRateCard } from '../../../freight-rate-card/FreightRateCard';
import { FreightRateCardSkeleton } from '../../../freight-rate-card/FreightRateCardSkeleton';
import { groupRates } from '../../../freight-rate-card/rate-grouping';

import type { LtlAddress, LtlQuote } from '../../../../types/ltl';
import type { ShipFromAddress } from '../../../../types/ship-from';
import type { WizardInsurance } from '../../freight-booking-wizard-types';

const SKELETON_ROW_COUNT = 3;

type WizardRatesPanelProps = {
  rates: LtlQuote[];
  isLoadingRates: boolean;
  rateError?: string;
  rateErrorCode?: string | null;
  connectedFreightProviderId?: number | null;
  selectedRate: LtlQuote | null;
  onSelectRate: (quote: LtlQuote) => void;
  onRefresh: () => void;
  insurance: WizardInsurance;
  onInsuranceChange: (next: WizardInsurance) => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;
};

export const WizardRatesPanel = ({
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  connectedFreightProviderId,
  selectedRate,
  onSelectRate,
  onRefresh,
  insurance,
  onInsuranceChange,
  shipFromAddress,
  destinationAddress,
}: WizardRatesPanelProps) => {
  const intl = useIntl();

  const grouped = groupRates(rates);
  const carrierRows = grouped.withoutInsurance;
  const hasRates = carrierRows.length > 0;
  const isQuoteIneligible = rateErrorCode === FREIGHT_QUOTE_ERROR_CODE.QuoteIneligible;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <Title fw={600} order={3}>
            {intl.formatMessage(wizardRatesPanelMessages.title)}
          </Title>
          <ActionIcon
            aria-label={intl.formatMessage(wizardRatesPanelMessages.refreshAriaLabel)}
            disabled={isLoadingRates}
            onClick={onRefresh}
            variant='ghost'
          >
            <FontAwesomeIcon className={clsx(styles.refreshIcon, isLoadingRates && styles.refreshIconSpinning)} icon={faArrowRotateRight} />
          </ActionIcon>
        </div>
      </div>
      <Stack className={styles.body} gap='md'>
        {rateError && !isQuoteIneligible && (
          <Alert color='red' data-testid='wizard-rate-error'>
            {rateError}
          </Alert>
        )}
        {isLoadingRates && (
          <div className={styles.ratesList}>
            {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <FreightRateCardSkeleton key={`wizard-rate-skeleton-${index}`} />
            ))}
          </div>
        )}
        {!isLoadingRates && isQuoteIneligible && !hasRates && (
          <div className={styles.emptyState}>
            <FreightNoCarriersMessage freightProviderId={connectedFreightProviderId} />
          </div>
        )}
        {!isLoadingRates && !hasRates && !rateError && !isQuoteIneligible && (
          <Text className={styles.emptyState} c='dimmed' data-testid='wizard-rates-empty'>
            {intl.formatMessage(wizardRatesPanelMessages.noRates)}
          </Text>
        )}
        {!isLoadingRates && hasRates && (
          <div className={styles.ratesList}>
            {carrierRows.map((carrierRow) => (
              <FreightRateCard
                carrierRow={carrierRow}
                destinationAddress={destinationAddress}
                key={carrierRow.carrierKey}
                onSelectRate={onSelectRate}
                selectedOfferId={selectedRate?.offerId}
                shipFromAddress={shipFromAddress}
              />
            ))}
            {/* TODO: wire pagination in follow-up; static placeholder for now. */}
            <Button className={styles.viewMore} disabled fullWidth variant='secondary'>
              {intl.formatMessage(wizardRatesPanelMessages.viewMoreRates, { count: carrierRows.length })}
            </Button>
          </div>
        )}
      </Stack>
      <div className={styles.stickyFooter}>
        <WizardInsuranceFooter insurance={insurance} onInsuranceChange={onInsuranceChange} />
        <WizardCostBreakdown selectedRate={selectedRate} />
        <div className={styles.footer}>
          <Text c='dimmed' size='xs'>
            {intl.formatMessage(wizardRatesPanelMessages.poweredBy)}
          </Text>
        </div>
      </div>
    </div>
  );
};
